import * as React from "react";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import * as SharedState from "../../../shared-state";
import * as PropertiesSelector from "../../../properties-selector";
import * as TopMenu from "./top-menu";
import * as KnownProperties from "../../tools/known-properties";
import * as LoadGroup from "./load-group";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import styled from "styled-components";
import { PropertyDefinitions, SettingsOverrides } from "../../tools";
import { PropertyValueSet } from "@genesys/property";
import { Dispatch } from "@typescript-tea/core";
import { State, Action } from "./state";
import { getAllLoads } from "./utils";
import { ViewMode } from "./state/types";
import { CardGrid, CardRow } from "./components";
import {
  PrimaryColors,
  GridIcon,
  SystemsIcon,
  BorderRadius
} from "@genesys/ui-elements";
import { ErrorSeverity } from "./types";

const Container = styled.div``;

const ApplicationTypeContainer = styled.div`
  width: max-content;

  > table {
    margin-top: -27px;
  }
`;

const StyledLoadGroup = styled.div<{ readonly isSelected: boolean }>`
  border-radius: ${BorderRadius.standard};
  border: 2.5px solid ${props => (props.isSelected ? "black" : "white")};
`;

const MainContentContainer = styled.div`
  overflow-y: scroll;
  height: 100%;

  ::-webkit-scrollbar {
    width: 7px;
    margin-right: 20px;
    opacity: 0;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #dde4f0;
    opacity: 1;
    border-radius: ${BorderRadius.scrollbarThumb};
    margin-right: 20px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ViewSelectionAnchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 23px;
  height: 23px;
  margin-right: 8px;
  border: 1px solid #f0f0f0;
  border-radius: ${BorderRadius.tiny};
  background: ${(props: { readonly isSelected: boolean }) =>
    props.isSelected ? PrimaryColors.lightBlue : "inherit"};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.03);
  cursor: pointer;
  img {
    margin: 0;
  }
  :hover {
    background: ${PrimaryColors.lightBlue};
  }
`;

export function View(props: {
  readonly sharedState: SharedState.State;
  readonly state: State;
  readonly isDisabled: boolean;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly climateSettings: PropertyValueSet.PropertyValueSet;

  readonly disableOpcButtons: boolean;
  readonly propertySelectorState: PropertiesSelector.State;
  readonly currentTopLevelSettingsOverrides: SettingsOverrides;

  readonly dispatch: Dispatch<Action>;
  readonly propertieSelectorDispatch: Dispatch<PropertiesSelector.Action>;
}) {
  return (
    <Container>
      <MainContent {...props} />
    </Container>
  );
}

function MainContent({
  state,
  sharedState,
  propertyDefinitions,
  propertySelectorState,
  isDisabled,
  currentTopLevelSettingsOverrides,
  dispatch,
  propertieSelectorDispatch
}: {
  readonly state: State;
  readonly sharedState: SharedState.State;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly isDisabled: boolean;
  readonly propertySelectorState: PropertiesSelector.State;
  readonly currentTopLevelSettingsOverrides: SettingsOverrides;
  readonly dispatch: Dispatch<Action>;
  readonly propertieSelectorDispatch: Dispatch<PropertiesSelector.Action>;
}) {
  const allLoads = React.useMemo(
    () => getAllLoads(state.moistureLoadClaimValue!),
    []
  );

  const selectableLoads = allLoads.filter(
    x =>
      x.visible &&
      (Object.keys(state.loadGroupState[x.sourceName]?.activeLoads || {})
        ?.length || 0) < x.maxInstanceCount
  );

  const loadGroups = Object.values(state.loadGroupState).sort(
    (a, b) => a.sortNo - b.sortNo
  );

  return (
    <>
      <TopMenu.View
        loadSources={selectableLoads}
        isDisabled={isDisabled}
        propertySelectorState={propertySelectorState}
        sharedState={sharedState}
        dispatch={dispatch}
        state={state}
      />

      <ApplicationTypeContainer>
        <PropertiesSelector.PropertiesSelectorView
          dispatch={propertieSelectorDispatch}
          translatePropertyName={propertyName =>
            sharedState.translate(
              LanguageTexts.globalPropertyName(propertyName)
            )
          }
          translatePropertyValue={(propertyName, propertyValue) =>
            sharedState.translate(
              LanguageTexts.globalPropertyName(
                propertyName + "_" + propertyValue
              )
            )
          }
          productId={""}
          sharedState={sharedState}
          propertiesInfo={applicationTypeDef(
            propertyDefinitions.find(
              definition => definition.name === KnownProperties.applicationType
            )!
          )}
          fieldGroup={"MoistureLoadTool.BuildingEditor"}
          isReadonly={isDisabled}
          showCodes={false}
          state={propertySelectorState}
        />
      </ApplicationTypeContainer>
      <ViewModeSelector
        selectedViewMode={state.viewModeState.viewMode}
        setViewMode={viewMode => dispatch(Action.setViewMode(viewMode))}
      />

      <MainContentContainer>
        <ViewModeContent
          allLoads={allLoads}
          currentTopLevelSettingsOverrides={currentTopLevelSettingsOverrides}
          dispatch={dispatch}
          isDisabled={isDisabled}
          loadGroups={loadGroups}
          propertySelectorState={propertySelectorState}
          selectedLoadGroup={state.selectedLoadGroup}
          sharedState={sharedState}
          viewMode={state.viewModeState.viewMode}
        />
      </MainContentContainer>
    </>
  );
}

function ViewModeContent({
  viewMode,
  sharedState,
  loadGroups,
  allLoads,
  isDisabled,
  selectedLoadGroup,
  propertySelectorState,
  currentTopLevelSettingsOverrides,
  dispatch
}: {
  readonly viewMode: ViewMode;
  readonly selectedLoadGroup: string | undefined;
  readonly sharedState: SharedState.State;
  readonly loadGroups: LoadGroup.State[];
  readonly allLoads: LoadGroup.LoadDef[];
  readonly isDisabled: boolean;
  readonly propertySelectorState: PropertiesSelector.State;
  readonly currentTopLevelSettingsOverrides: SettingsOverrides;
  readonly dispatch: Dispatch<Action>;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  if (viewMode === "grid") {
    function removeSelectedLoad() {
      if (selectedLoadGroup === undefined) {
        return;
      }
      dispatch(Action.setSelectedLoadGroup(undefined));
    }

    const onClickaway = (e: any) => {
      if ((e.target as any).nodeName === "BODY" || !selectedLoadGroup) {
        // e.stopPropagation();
        return;
      }
      removeSelectedLoad();
    };

    const createLoadGroupContent = (loadGroup: LoadGroup.State) => (
      <LoadGroupContainer
        selectedLoadGroup={selectedLoadGroup}
        loadGroup={loadGroup}
        dispatch={dispatch}
        viewMode={viewMode}
        allLoads={allLoads}
        isDisabled={isDisabled}
        sharedState={sharedState}
        currentTopLevelSettingsOverrides={currentTopLevelSettingsOverrides}
        propertySelectorState={propertySelectorState}
        scrollRef={scrollRef}
      />
    );

    return (
      <CardGrid
        items={loadGroups.map(loadGroup => (
          <>
            {selectedLoadGroup === loadGroup.group ? (
              <ClickAwayListener onClickAway={onClickaway} mouseEvent="onClick">
                <div ref={scrollRef}>{createLoadGroupContent(loadGroup)}</div>
              </ClickAwayListener>
            ) : (
              createLoadGroupContent(loadGroup)
            )}
          </>
        ))}
      />
    );
  } else if (viewMode === "row") {
    return (
      <CardRow
        items={loadGroups.map(x => {
          const loadDef = allLoads.find(y => y.sourceName === x.group)!;
          return {
            groupName: sharedState.translate(
              LanguageTexts.globalPropertyName(loadDef.title)
            ),
            infoText: sharedState.translate(
              LanguageTexts.globalPropertyName(loadDef.description)
            ),
            item: (
              <LoadGroup.View
                viewMode={viewMode}
                loadDef={loadDef}
                state={x}
                selectedLoadGroup={selectedLoadGroup}
                handleSelectLoadGroup={group => {
                  dispatch(Action.setSelectedLoadGroup(group));
                }}
                isDisabled={isDisabled}
                sharedState={sharedState}
                currentTopLevelCaseOverrides={currentTopLevelSettingsOverrides}
                currentTopLevelMoistureLoadSettings={PropertiesSelector.getSelectedProperties(
                  propertySelectorState
                )}
                dispatch={Dispatch.map(
                  action => Action.dispatchLoadGroup(x.group, action),
                  dispatch
                )}
              />
            ),
            currentMoistureLoadSettings:
              PropertiesSelector.getSelectedProperties(propertySelectorState),
            state: x,
            loadDef: loadDef
          };
        })}
      />
    );
  }

  return <div>VewMode UnAvaliable</div>;
}

function LoadGroupContainer({
  selectedLoadGroup,
  loadGroup,
  viewMode,
  allLoads,
  isDisabled,
  sharedState,
  currentTopLevelSettingsOverrides,
  propertySelectorState,
  scrollRef,
  dispatch
}: {
  readonly viewMode: ViewMode;
  readonly selectedLoadGroup: string | undefined;
  readonly sharedState: SharedState.State;
  readonly allLoads: LoadGroup.LoadDef[];
  readonly isDisabled: boolean;
  readonly propertySelectorState: PropertiesSelector.State;
  readonly currentTopLevelSettingsOverrides: SettingsOverrides;
  readonly loadGroup: LoadGroup.State;
  readonly scrollRef: React.RefObject<HTMLDivElement>;
  readonly dispatch: Dispatch<Action>;
}): JSX.Element {
  return (
    <StyledLoadGroup
      onClick={e => {
        if (selectedLoadGroup !== loadGroup.group) {
          dispatch(Action.setSelectedLoadGroup(loadGroup.group));
        }
        e.stopPropagation();
      }}
      isSelected={selectedLoadGroup === loadGroup.group}
    >
      <LoadGroup.View
        viewMode={viewMode}
        loadDef={allLoads.find(x => x.sourceName === loadGroup.group)!}
        state={loadGroup}
        isDisabled={isDisabled}
        sharedState={sharedState}
        currentTopLevelCaseOverrides={currentTopLevelSettingsOverrides}
        currentTopLevelMoistureLoadSettings={PropertiesSelector.getSelectedProperties(
          propertySelectorState
        )}
        scrollRef={scrollRef}
        dispatch={Dispatch.map(
          action => Action.dispatchLoadGroup(loadGroup.group, action),
          dispatch
        )}
      />
    </StyledLoadGroup>
  );
}

function ViewModeSelector({
  selectedViewMode,
  setViewMode
}: {
  readonly selectedViewMode: ViewMode;
  readonly setViewMode: (viewMode: ViewMode) => void;
}) {
  return (
    <div style={{ display: "flex", marginBottom: "10px" }}>
      <ViewSelectionAnchor
        isSelected={selectedViewMode === "grid"}
        onClick={() => {
          setViewMode("grid");
        }}
      >
        <GridIcon height="1.0rem" />
      </ViewSelectionAnchor>
      <ViewSelectionAnchor
        isSelected={selectedViewMode === "row"}
        onClick={() => {
          setViewMode("row");
        }}
      >
        <SystemsIcon height="1.0rem" />
      </ViewSelectionAnchor>
    </div>
  );
}

function applicationTypeDef(
  applicationConfigurationDef: PropertiesSelector.PropertyInfo
) {
  return [
    {
      ...applicationConfigurationDef,
      items: [...applicationConfigurationDef.items].sort(
        (a, b) => b.sortNo - a.sortNo
      )
    }
  ];
}

export function GetErrorSeverity(
  state: State,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet
): ErrorSeverity {
  const loadGroupsStates = Object.values(state.loadGroupState);
  const allLoads = React.useMemo(
    () => getAllLoads(state.moistureLoadClaimValue!),
    []
  );

  const errorOrWarnings = loadGroupsStates.map(loadGroupState =>
    LoadGroup.GetErrorSeverity(
      loadGroupState,
      allLoads.find(load => load.sourceName === loadGroupState.group)!,
      currentTopLevelMoistureLoadSettings
    )
  );
  return errorOrWarnings.includes("error")
    ? "error"
    : errorOrWarnings.includes("warning")
    ? "warning"
    : "none";
}
//tslint:disable-next-line
